import React, { FC } from 'react';
import { Translate } from 'react-localize-redux';
import { PulseLoader } from 'react-spinners';
import { Alert } from 'reactstrap';

import { formatShortDateRange } from '../../helpers/datetime';
import useTranslate from '../../hooks/useTranslate';
import { useAccommodation, useAccommodationItems } from '../../services/apis/accommodation/api';
import { AccommodationType } from '../../services/apis/accommodation/types';
import AccommodationInfo from '../Accommodation/AccommodationInfo';
import AccommodationItem from '../AccommodationItem/AccommodationItem';
import Button from '../Button/Button';

export interface AccommodationProps {
  accommodationId: AccommodationType['id'];
  checkinDate: string;
  checkoutDate: string;
  onShowMap?: () => void;
  mapVisible?: boolean;
}

const AccommodationAvailability: FC<AccommodationProps> = props => {
  const { accommodationId, checkinDate, checkoutDate, onShowMap, mapVisible } = props;

  const { translate, activeLanguage } = useTranslate();

  const { data: accommodation, isLoading: loadingAccommodation } =
    useAccommodation(accommodationId);
  const { data: accommodationItems, isLoading: loadingAccommodationItems } = useAccommodationItems(
    accommodationId,
    checkinDate,
    checkoutDate
  );

  const hasAccommodationData =
    accommodation && accommodationItems && accommodationItems?.length > 0;

  const availableAccommodationItems = accommodationItems?.filter(item => item.available) ?? [];
  const notAvailableAccommodationItems = accommodationItems?.filter(item => !item.available) ?? [];

  return (
    <div className="my-4" style={{ minHeight: 230 }}>
      {hasAccommodationData ? (
        <>
          <AccommodationInfo accommodation={accommodation} collapseDescription />
          <h3>
            {String(
              translate('accommodation.availability-dates', {
                dates: formatShortDateRange(checkinDate, checkoutDate, activeLanguage.code),
              })
            )}
          </h3>
          <div className="d-flex flex-column gap-4 mb-5">
            {[...availableAccommodationItems, ...notAvailableAccommodationItems].map(item => (
              <AccommodationItem
                // force re-render to reset component's internal state
                // key={`${item.id}_${checkinDate}_${checkoutDate}`}
                key={item.id}
                item={item}
                disabled={loadingAccommodationItems}
                accommodationId={accommodationId}
                checkinDate={checkinDate}
                checkoutDate={checkoutDate}
              />
            ))}
          </div>

          {onShowMap && !mapVisible && (
            <div className="text-end">
              <Button primary onClick={onShowMap} className="mt-3 mr-1">
                <Translate id="District.ShowMap" />
              </Button>
            </div>
          )}
        </>
      ) : loadingAccommodation ? (
        <div className="text-center">
          <PulseLoader color="currentColor" />
        </div>
      ) : (
        <Alert color="info">{String(translate('accommodation.notAvailableForDates'))}</Alert>
      )}
    </div>
  );
};

export default AccommodationAvailability;
